import { defineModule, localActionContext, localGetterContext } from 'direct-vuex'
import type { DocumentNode } from 'graphql'
import { VueLogger } from 'vue-logger-plugin'
import { ActionContext } from 'vuex'

import { NO_FILTER_SENTINEL } from '@/constants/appConstants'
import { CreateAliyahParameters } from '@/util/Aliyah'
import { CreateHaftarahParameters } from '@/util/Haftarah'
import { cloneRaw, normalizeEmail, normalizeUsername } from '@/util/util'

import {
  AliyahType,
  BarMitzvahType,
  HaftarahType,
  ParashahType,
  PracticeStatsType,
  Query,
  SectionType,
  SentenceGroupCategoryType,
  SentenceGroupType,
  SentenceType,
  StudentPracticeOverviewType,
} from '../../types/types'
import AddAliyahToBarMitzvah from '../mutations/AddAliyahToBarMitzvah.graphql'
import AddOrUpdateBarMitzvah from '../mutations/AddOrUpdateBarMitzvah.graphql'
import AddSentenceGroupToBarMitzvah from '../mutations/AddSentenceGroupToBarMitzvah.graphql'
import CreateOrUpdateAliyah from '../mutations/CreateOrUpdateAliyah.graphql'
import CreateOrUpdateHaftarah from '../mutations/CreateOrUpdateHaftarah.graphql'
import CreateOrUpdateSentenceGroupCategory from '../mutations/CreateOrUpdateSentenceGroupCategory.graphql'
import CreateSentenceGroup from '../mutations/CreateSentenceGroup.graphql'
import DeleteAliyah from '../mutations/DeleteAliyah.graphql'
import DeleteBarMitzvah from '../mutations/DeleteBarMitzvah.graphql'
import DeleteHaftarah from '../mutations/DeleteHaftarah.graphql'
import DeleteSentenceGroup from '../mutations/DeleteSentenceGroup.graphql'
import DeleteSentenceGroupCategory from '../mutations/DeleteSentenceGroupCategory.graphql'
import RemoveAliyahFromBarMitzvah from '../mutations/RemoveAliyahFromBarMitzvah.graphql'
import RemoveSentenceGroupFromBarMitzvah from '../mutations/RemoveSentenceGroupFromBarMitzvah.graphql'
import SetHaftarahToBarMitzvah from '../mutations/SetHaftarahToBarMitzvah.graphql'
import UnsetHaftarahFromBarMitzvah from '../mutations/UnsetHaftarahFromBarMitzvah.graphql'
import AliyotDetailedQuery from '../queries/AliyotDetailedQuery.graphql'
import BarMitzvahQuery from '../queries/BarMitzvahQuery.graphql'
import HaftarotQuery from '../queries/HaftarotQuery.graphql'
import MyBarMitzvotQuery from '../queries/MyBarMitzvotQuery.graphql'
import MyLightBarMitzvotQuery from '../queries/MyLightBarMitzvotQuery.graphql'
import PracticeOverviewQuery from '../queries/PracticeOverviewQuery.graphql'
import PracticeStatsQuery from '../queries/PracticeStatsQuery.graphql'
import SectionsQuery from '../queries/SectionsQuery.graphql'
import SentenceGroupCategoriesQuery from '../queries/SentenceGroupCategoriesQuery.graphql'
import SentenceGroupsQuery from '../queries/SentenceGroupsQuery.graphql'
import { MutationPayload, ReportingMutation, runStoreMutation } from '../store/mutationHelpers'
import { querySentenceGroupSentences } from '../store/queryHelpers'
import { useApollo } from '../util/apolloClient'
const { apolloClient } = useApollo()

export interface TeacherState {
  barMitzvot: Array<BarMitzvahType>
  lightBarMitzvot: Array<BarMitzvahType>
  practiceOverviews: Array<StudentPracticeOverviewType>
  practiceStats: PracticeStatsType | null
  sentenceGroupCategories: Array<SentenceGroupCategoryType>
  sentenceGroups: Array<SentenceGroupType>
  aliyot: Array<AliyahType>
  haftarot: Array<HaftarahType>
  sections: Array<SectionType>
  updating: boolean
  loading: boolean
  logger: VueLogger | null
}

const resetState = (): TeacherState => {
  return {
    aliyot: [],
    barMitzvot: [],
    haftarot: [],
    lightBarMitzvot: [],
    loading: false,
    logger: null,
    practiceOverviews: [],
    practiceStats: null,
    sections: [],
    sentenceGroupCategories: [],
    sentenceGroups: [],
    updating: false,
  }
}

// --- Shared helpers for reducing action/mutation boilerplate ---

/**
 * Run a GraphQL mutation. Resolves true on success. On failure it shows the reason in the
 * snackbar and resolves false, so no caller can drop a failure.
 */
async function runMutation<K extends ReportingMutation>(
  context: ActionContext<TeacherState, unknown>,
  mutation: DocumentNode,
  variables: Record<string, unknown>,
  dataKey: K,
  onSuccess?: (result: MutationPayload<K>) => void,
): Promise<boolean> {
  // eslint-disable-next-line no-use-before-define
  const { commit } = teacherActionContext(context)
  commit.setUpdating(true)
  try {
    const payload = await runStoreMutation(context, mutation, variables, dataKey)
    if (payload) {
      onSuccess?.(payload)
    }
    return !!payload
  } finally {
    commit.setUpdating(false)
  }
}

/** Upsert an item in an array: replace if matcher finds it, otherwise push. */
function upsertItem<T>(array: T[], item: T, matcher: (existing: T) => boolean): void {
  const index = array.findIndex(matcher)
  if (index >= 0) {
    array[index] = cloneRaw(item) as T
  } else {
    array.push(cloneRaw(item) as T)
  }
}

/** Remove an item from an array by matcher. */
function removeItem<T>(array: T[], matcher: (item: T) => boolean): void {
  const index = array.findIndex(matcher)
  if (index >= 0) {
    array.splice(index, 1)
  }
}

/** Apply a mutation to a bar mitzvah in both barMitzvot and lightBarMitzvot arrays.
 *  Since mergeBarMitzvah puts full data into lightBarMitzvot, mutations must update
 *  both arrays to prevent stale data when a student is re-selected from the dropdown. */
function withBarMitzvah(state: TeacherState, username: string, callerName: string, fn: (bm: BarMitzvahType) => void): void {
  let found = false
  for (const array of [state.barMitzvot, state.lightBarMitzvot]) {
    const bm = array.find((b) => b.student.username === username)
    if (bm) {
      fn(bm)
      found = true
    }
  }
  if (!found) {
    console.log(`unexpected !bm in ${callerName}`)
  }
}

/** Filter out hidden bar mitzvot and sort by student last name. */
function visibleSortedByLastName(barMitzvot: BarMitzvahType[]): BarMitzvahType[] {
  if (!barMitzvot) {
    return []
  }
  return barMitzvot.filter((b) => !b.hidden).toSorted((a, b) => (a.student.lastName ?? '').localeCompare(b.student.lastName ?? ''))
}

const teacher = defineModule({
  actions: {
    async addAliyahToBarMitzvah(context, payload: { barMitzvah: BarMitzvahType; aliyah: AliyahType }) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      return runMutation(
        context,
        AddAliyahToBarMitzvah,
        { aliyah: payload.aliyah.id, barMitzvah: payload.barMitzvah.student.username },
        'addAliyahToBarMitzvah',
        (result) => commit.addAliyahToBarMitzvah({ aliyah: result.aliyah!, barMitzvah: payload.barMitzvah }),
      )
    },

    async addOrUpdateBarMitzvah(
      context,
      payload: {
        user: {
          email: string
          firstName: string
          lastName: string
          username: string
        }
        date: string
        hidden: boolean
        kippahReminder: boolean
        practiceTracking: boolean
        torahMelodyID: number
        haftarahMelodyID: number
        sentenceGroupMelodyID: number
        pronunciationID: number
        update: boolean
      },
    ): Promise<boolean> {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)

      return runMutation(
        context,
        AddOrUpdateBarMitzvah,
        {
          date: payload.date,
          haftarahMelody: payload.haftarahMelodyID,
          hidden: payload.hidden,
          kippahReminder: payload.kippahReminder,
          practiceTracking: payload.practiceTracking,
          pronunciation: payload.pronunciationID,
          sentenceGroupMelody: payload.sentenceGroupMelodyID,
          torahMelody: payload.torahMelodyID,
          update: payload.update,
          userData: {
            email: normalizeEmail(payload.user.email),
            firstName: payload.user.firstName,
            lastName: payload.user.lastName,
            username: normalizeUsername(payload.user.username),
          },
        },
        'addOrUpdateBarMitzvah',
        (result) => commit.addOrUpdateBarMitzvah(result.barMitzvah!),
      )
    },

    async addSentenceGroupToBarMitzvah(context, payload: { barMitzvah: BarMitzvahType; sentenceGroup: SentenceGroupType }) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      return runMutation(
        context,
        AddSentenceGroupToBarMitzvah,
        { barMitzvah: payload.barMitzvah.student.username, sentenceGroup: payload.sentenceGroup.reading.id },
        'addSentenceGroupToBarMitzvah',
        (result) => commit.addSentenceGroupToBarMitzvah({ barMitzvah: payload.barMitzvah, sentenceGroup: result.sentenceGroup! }),
      )
    },

    async createAliyah(context, payload: CreateAliyahParameters) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      return runMutation(context, CreateOrUpdateAliyah, { ...payload, parashah: payload.parashah.reading.id }, 'createAliyah', (result) =>
        commit.addAliyah({ aliyah: result.aliyah! }),
      )
    },

    async createHaftarah(context, payload: CreateHaftarahParameters) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      return runMutation(
        context,
        CreateOrUpdateHaftarah,
        { ...payload, parashah: payload.parashah.reading.id },
        'createHaftarah',
        (result) => commit.addHaftarah({ haftarah: result.haftarah! }),
      )
    },

    async createOrUpdateSentenceGroupCategory(context, payload: { id?: string; description: string; order?: number }) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      return runMutation(
        context,
        CreateOrUpdateSentenceGroupCategory,
        { description: payload.description, id: payload.id, order: payload.order },
        'createOrUpdateSentenceGroupCategory',
        (result) => commit.upsertSentenceGroupCategory({ category: result.sentenceGroupCategory! }),
      )
    },

    async createSentenceGroup(
      context,
      payload: {
        id: string | null
        description: string
        username: string
        text: Array<string>
        category?: string
      },
    ) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      return runMutation(
        context,
        CreateSentenceGroup,
        {
          category: payload.category || null,
          description: payload.description,
          id: payload.id || NO_FILTER_SENTINEL,
          text: payload.text,
          username: payload.username,
        },
        'createSentenceGroup',
        (result) => commit.addSentenceGroup({ sentenceGroup: result.sentenceGroup! }),
      )
    },

    async deleteAliyah(context, payload: { aliyah: AliyahType }) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      return runMutation(context, DeleteAliyah, { id: payload.aliyah.id }, 'deleteAliyah', () =>
        commit.deleteAliyah({ aliyah: payload.aliyah }),
      )
    },

    async deleteAliyahFromBarMitzvah(context, payload: { barMitzvah: BarMitzvahType; aliyah: AliyahType }) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      return runMutation(
        context,
        RemoveAliyahFromBarMitzvah,
        { aliyah: payload.aliyah.id, barMitzvah: payload.barMitzvah.student.username },
        'removeAliyahFromBarMitzvah',
        () => commit.deleteAliyahFromBarMitzvah({ aliyah: payload.aliyah, barMitzvah: payload.barMitzvah }),
      )
    },

    async deleteBarMitzvah(context, payload: { barMitzvah: BarMitzvahType }) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)

      return runMutation(context, DeleteBarMitzvah, { username: payload.barMitzvah.student.username }, 'deleteBarMitzvah', () =>
        commit.deleteBarMitzvah(payload.barMitzvah),
      )
    },

    async deleteHaftarah(context, payload: { haftarah: HaftarahType }) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      return runMutation(context, DeleteHaftarah, { id: payload.haftarah.reading.id }, 'deleteHaftarah', () =>
        commit.deleteHaftarah({ haftarah: payload.haftarah }),
      )
    },

    async deleteSentenceGroup(context, payload: { sentenceGroup: SentenceGroupType }) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      return runMutation(context, DeleteSentenceGroup, { id: payload.sentenceGroup.reading.id }, 'deleteSentenceGroup', () =>
        commit.deleteSentenceGroup({ sentenceGroup: payload.sentenceGroup }),
      )
    },

    async deleteSentenceGroupCategory(context, payload: { id: string }) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      return runMutation(context, DeleteSentenceGroupCategory, { id: payload.id }, 'deleteSentenceGroupCategory', () =>
        commit.removeSentenceGroupCategory({ id: payload.id }),
      )
    },

    async deleteSentenceGroupFromBarMitzvah(context, payload: { barMitzvah: BarMitzvahType; sentenceGroup: SentenceGroupType }) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      return runMutation(
        context,
        RemoveSentenceGroupFromBarMitzvah,
        { barMitzvah: payload.barMitzvah.student.username, sentenceGroup: payload.sentenceGroup.reading.id },
        'removeSentenceGroupFromBarMitzvah',
        () => commit.deleteSentenceGroupFromBarMitzvah({ barMitzvah: payload.barMitzvah, sentenceGroup: payload.sentenceGroup }),
      )
    },

    async fetchAliyot(context, payload: { parashah: ParashahType | null; mine: boolean }) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      commit.setLoading(true)
      try {
        const response = await apolloClient.query<Pick<Query, 'aliyot'>>({
          fetchPolicy: 'network-only',
          query: AliyotDetailedQuery,
          variables: {
            mine: payload.mine,
            parashah: payload.parashah ? payload.parashah.reading.id : -1,
          },
        })
        commit.setAliyot(cloneRaw((response.data?.aliyot?.filter(Boolean) as AliyahType[]) ?? []))
      } catch (error) {
        console.error('fetchAliyot failed:', error)
      } finally {
        commit.setLoading(false)
      }
    },

    async fetchBarMitzvah(context, payload: { id: number }) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      commit.setLoading(true)
      try {
        const response = await apolloClient.query<Pick<Query, 'barMitzvah'>>({
          fetchPolicy: 'network-only',
          query: BarMitzvahQuery,
          variables: {
            id: payload.id,
          },
        })
        const bm = response.data?.barMitzvah
        if (bm) {
          commit.mergeBarMitzvah(bm)
        }
      } catch (error) {
        console.error('fetchBarMitzvah failed:', error)
      } finally {
        commit.setLoading(false)
      }
    },

    async fetchBarMitzvot(context) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      commit.setLoading(true)
      try {
        const response = await apolloClient.query<Pick<Query, 'myBarMitzvot'>>({
          fetchPolicy: 'network-only',
          query: MyBarMitzvotQuery,
        })
        commit.setBarMitzvot((response.data?.myBarMitzvot?.filter(Boolean) as BarMitzvahType[]) ?? [])
      } catch (error) {
        console.error('fetchBarMitzvot failed:', error)
      } finally {
        commit.setLoading(false)
      }
    },

    /**
     * Resolve a "light" BarMitzvah (as emitted by StudentSelect, with aliyot === undefined) to the
     * full object. Returns it unchanged if already full; otherwise fetches by student id (the
     * barMitzvah(id) resolver keys on student id) and returns the merged copy from lightBarMitzvot.
     */
    async fetchFullBarMitzvah(context, lightBm: BarMitzvahType): Promise<BarMitzvahType | null> {
      // eslint-disable-next-line no-use-before-define
      const { dispatch, getters } = teacherActionContext(context)
      if (lightBm.aliyot !== undefined) {
        return lightBm
      }
      await dispatch.fetchBarMitzvah({ id: parseInt(lightBm.student.id, 10) })
      return getters.lightBarMitzvot.find((b: BarMitzvahType) => b.student.username === lightBm.student.username) ?? null
    },

    async fetchHaftarot(context, payload: { parashah: ParashahType | null; mine: boolean }) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      commit.setLoading(true)
      try {
        const response = await apolloClient.query<Pick<Query, 'haftarot'>>({
          fetchPolicy: 'network-only',
          query: HaftarotQuery,
          variables: {
            mine: payload.mine,
            parashah: payload.parashah ? payload.parashah.reading.id : -1,
          },
        })
        commit.setHaftarot((response.data?.haftarot?.filter(Boolean) as HaftarahType[]) ?? [])
      } catch (error) {
        console.error('fetchHaftarot failed:', error)
      } finally {
        commit.setLoading(false)
      }
    },

    async fetchLightBarMitzvot(context) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      commit.setLoading(true)
      try {
        const response = await apolloClient.query<Pick<Query, 'myBarMitzvot'>>({
          fetchPolicy: 'network-only',
          query: MyLightBarMitzvotQuery,
        })
        commit.setLightBarMitzvot((response.data?.myBarMitzvot?.filter(Boolean) as BarMitzvahType[]) ?? [])
      } catch (error) {
        console.error('fetchLightBarMitzvot failed:', error)
      } finally {
        commit.setLoading(false)
      }
    },

    async fetchMySentenceGroupSentences(context, payload: { sentenceGroup: SentenceGroupType }) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      commit.setLoading(true)
      try {
        commit.setSentenceGroupSentences(await querySentenceGroupSentences(payload.sentenceGroup))
      } catch (error) {
        console.error('fetchMySentenceGroupSentences failed:', error)
      } finally {
        commit.setLoading(false)
      }
    },

    async fetchPracticeOverview(context) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      try {
        const response = await apolloClient.query<Pick<Query, 'practiceOverview'>>({
          fetchPolicy: 'network-only',
          query: PracticeOverviewQuery,
        })
        commit.setPracticeOverviews((response.data?.practiceOverview?.filter(Boolean) as StudentPracticeOverviewType[]) ?? [])
      } catch (error) {
        console.error('fetchPracticeOverview failed:', error)
      }
    },

    async fetchPracticeStats(context, payload: { studentId: string }) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      try {
        const response = await apolloClient.query<Pick<Query, 'practiceStats'>>({
          fetchPolicy: 'network-only',
          query: PracticeStatsQuery,
          variables: { studentId: payload.studentId },
        })
        commit.setPracticeStats((response.data?.practiceStats ?? null) as PracticeStatsType | null)
      } catch (error) {
        console.error('fetchPracticeStats failed:', error)
      }
    },

    async fetchSections(context) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      commit.setLoading(true)
      try {
        const response = await apolloClient.query<Pick<Query, 'sections'>>({
          fetchPolicy: 'network-only',
          query: SectionsQuery,
        })
        commit.setSections((response.data?.sections?.filter(Boolean) as SectionType[]) ?? [])
      } catch (error) {
        console.error('fetchSections failed:', error)
      } finally {
        commit.setLoading(false)
      }
    },

    async fetchSentenceGroupCategories(context) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      commit.setLoading(true)
      try {
        const response = await apolloClient.query<Pick<Query, 'sentenceGroupCategories'>>({
          fetchPolicy: 'network-only',
          query: SentenceGroupCategoriesQuery,
        })
        commit.setSentenceGroupCategories((response.data?.sentenceGroupCategories?.filter(Boolean) as SentenceGroupCategoryType[]) ?? [])
      } catch (error) {
        console.error('fetchSentenceGroupCategories failed:', error)
      } finally {
        commit.setLoading(false)
      }
    },

    async fetchSentenceGroups(context, payload: { mine: boolean } = { mine: false }) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      commit.setLoading(true)
      try {
        const response = await apolloClient.query<Pick<Query, 'sentenceGroups'>>({
          fetchPolicy: 'network-only',
          query: SentenceGroupsQuery,
          variables: {
            mine: payload.mine,
          },
        })
        commit.setSentenceGroups((response.data?.sentenceGroups?.filter(Boolean) as SentenceGroupType[]) ?? [])
      } catch (error) {
        console.error('fetchSentenceGroups failed:', error)
      } finally {
        commit.setLoading(false)
      }
    },

    reset(context) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      commit.reset()
    },

    async setHaftarahToBarMitzvah(context, payload: { barMitzvah: BarMitzvahType; haftarah: HaftarahType }) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      return runMutation(
        context,
        SetHaftarahToBarMitzvah,
        { barMitzvah: payload.barMitzvah.student.username, haftarah: payload.haftarah.reading.id },
        'setHaftarahToBarMitzvah',
        (result) => commit.setHaftarahToBarMitzvah({ barMitzvah: payload.barMitzvah, haftarah: result.haftarah! }),
      )
    },

    setLogger(context, logger: VueLogger) {
      const { commit } = teacherActionContext(context)
      commit.setLogger(logger)
    },

    async unsetHaftarahFromBarMitzvah(context, payload: { barMitzvah: BarMitzvahType }) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = teacherActionContext(context)
      return runMutation(
        context,
        UnsetHaftarahFromBarMitzvah,
        { barMitzvah: payload.barMitzvah.student.username },
        'unsetHaftarahFromBarMitzvah',
        () => commit.unsetHaftarahFromBarMitzvah({ barMitzvah: payload.barMitzvah }),
      )
    },
  },

  getters: {
    aliyot: (...args): Array<AliyahType> => {
      // eslint-disable-next-line no-use-before-define
      const { state } = teacherGetterContext(args)
      return state.aliyot
    },

    barMitzvot: (...args): Array<BarMitzvahType> => {
      // eslint-disable-next-line no-use-before-define
      const { state } = teacherGetterContext(args)
      return state.barMitzvot.toSorted((a, b) => (a.student.firstName ?? '').localeCompare(b.student.firstName ?? ''))
    },

    haftarot: (...args): Array<HaftarahType> => {
      // eslint-disable-next-line no-use-before-define
      const { state } = teacherGetterContext(args)
      return state.haftarot
    },

    lightBarMitzvot: (...args): Array<BarMitzvahType> => {
      // eslint-disable-next-line no-use-before-define
      const { state } = teacherGetterContext(args)
      return state.lightBarMitzvot.toSorted((a, b) => (a.student.lastName ?? '').localeCompare(b.student.lastName ?? ''))
    },

    loading(...args): boolean {
      // eslint-disable-next-line no-use-before-define
      const { state } = teacherGetterContext(args)
      return state.loading
    },

    practiceOverviews: (...args): Array<StudentPracticeOverviewType> => {
      // eslint-disable-next-line no-use-before-define
      const { state } = teacherGetterContext(args)
      return state.practiceOverviews
    },

    practiceStats: (...args): PracticeStatsType | null => {
      // eslint-disable-next-line no-use-before-define
      const { state } = teacherGetterContext(args)
      return state.practiceStats
    },

    sections: (...args): Array<SectionType> => {
      // eslint-disable-next-line no-use-before-define
      const { state } = teacherGetterContext(args)
      return state.sections
    },

    sentenceGroupCategories: (...args): Array<SentenceGroupCategoryType> => {
      // eslint-disable-next-line no-use-before-define
      const { state } = teacherGetterContext(args)
      return state.sentenceGroupCategories.toSorted((a, b) => a.order - b.order)
    },

    sentenceGroups: (...args): Array<SentenceGroupType> => {
      // eslint-disable-next-line no-use-before-define
      const { state } = teacherGetterContext(args)
      return state.sentenceGroups.toSorted((a, b) => (a.reading.description ?? '').localeCompare(b.reading.description ?? ''))
    },

    updating(...args): boolean {
      // eslint-disable-next-line no-use-before-define
      const { state } = teacherGetterContext(args)
      return state.updating
    },

    visibleBarMitzvot: (...args): Array<BarMitzvahType> => {
      // eslint-disable-next-line no-use-before-define
      const { state } = teacherGetterContext(args)
      return visibleSortedByLastName(state.barMitzvot)
    },

    visibleLightBarMitzvot: (...args): Array<BarMitzvahType> => {
      // eslint-disable-next-line no-use-before-define
      const { state } = teacherGetterContext(args)
      return visibleSortedByLastName(state.lightBarMitzvot)
    },
  },

  mutations: {
    addAliyah(state: TeacherState, payload: { aliyah: AliyahType }) {
      upsertItem(state.aliyot, payload.aliyah, (a) => a.id === payload.aliyah.id)
    },

    addAliyahToBarMitzvah(state: TeacherState, payload: { barMitzvah: BarMitzvahType; aliyah: AliyahType }) {
      withBarMitzvah(state, payload.barMitzvah.student.username, 'addAliyahToBarMitzvah', (bm) => {
        bm.aliyot.push(cloneRaw(payload.aliyah))
      })
    },

    addHaftarah(state: TeacherState, payload: { haftarah: HaftarahType }) {
      upsertItem(state.haftarot, payload.haftarah, (h) => h.reading.id === payload.haftarah.reading.id)
    },

    addOrUpdateBarMitzvah(state: TeacherState, barMitzvah: BarMitzvahType) {
      const bmMatcher = (bm: BarMitzvahType) => bm.student.username === barMitzvah.student.username
      upsertItem(state.barMitzvot, barMitzvah, bmMatcher)
      upsertItem(state.lightBarMitzvot, barMitzvah, bmMatcher)
    },

    addSentenceGroup(state: TeacherState, payload: { sentenceGroup: SentenceGroupType }) {
      console.log(`addSentenceGroup, payload.sentenceGroup =`)
      console.log(payload.sentenceGroup)
      console.log(`addSentenceGroup, state.sentenceGroup =`)
      console.log(state.sentenceGroups)
      upsertItem(state.sentenceGroups, payload.sentenceGroup, (s) => s.reading.id === payload.sentenceGroup.reading.id)
      console.log(`addSentenceGroup done`)
    },

    addSentenceGroupToBarMitzvah(state: TeacherState, payload: { barMitzvah: BarMitzvahType; sentenceGroup: SentenceGroupType }) {
      withBarMitzvah(state, payload.barMitzvah.student.username, 'addSentenceGroupToBarMitzvah', (bm) => {
        bm.sentenceGroups.push(cloneRaw(payload.sentenceGroup))
      })
    },

    deleteAliyah(state: TeacherState, payload: { aliyah: AliyahType }) {
      removeItem(state.aliyot, (a) => a.id === payload.aliyah.id)
    },

    deleteAliyahFromBarMitzvah(state: TeacherState, payload: { barMitzvah: BarMitzvahType; aliyah: AliyahType }) {
      withBarMitzvah(state, payload.barMitzvah.student.username, 'deleteAliyahFromBarMitzvah', (bm) => {
        removeItem(bm.aliyot, (a) => a.id === payload.aliyah.id)
      })
    },

    deleteBarMitzvah(state: TeacherState, barMitzvah: BarMitzvahType) {
      const bmMatcher = (b: BarMitzvahType) => b.student.username === barMitzvah.student.username
      removeItem(state.barMitzvot, bmMatcher)
      removeItem(state.lightBarMitzvot, bmMatcher)
    },

    deleteHaftarah(state: TeacherState, payload: { haftarah: HaftarahType }) {
      removeItem(state.haftarot, (h) => h.reading.id === payload.haftarah.reading.id)
    },

    deleteSentenceGroup(state: TeacherState, payload: { sentenceGroup: SentenceGroupType }) {
      removeItem(state.sentenceGroups, (s) => s.reading.id === payload.sentenceGroup.reading.id)
    },

    deleteSentenceGroupFromBarMitzvah(state: TeacherState, payload: { barMitzvah: BarMitzvahType; sentenceGroup: SentenceGroupType }) {
      withBarMitzvah(state, payload.barMitzvah.student.username, 'deleteSentenceGroupFromBarMitzvah', (bm) => {
        removeItem(bm.sentenceGroups, (sg) => sg.reading.id === payload.sentenceGroup.reading.id)
      })
    },

    mergeBarMitzvah(state: TeacherState, barMitzvah: BarMitzvahType) {
      const bmMatcher = (bm: BarMitzvahType) => bm.student.username === barMitzvah.student.username
      removeItem(state.barMitzvot, bmMatcher)
      state.barMitzvot.push(cloneRaw(barMitzvah))
      removeItem(state.lightBarMitzvot, bmMatcher)
      state.lightBarMitzvot.push(cloneRaw(barMitzvah))
    },

    removeSentenceGroupCategory(state: TeacherState, payload: { id: string }) {
      removeItem(state.sentenceGroupCategories, (c) => c.id === payload.id)
    },

    reset(state: TeacherState) {
      Object.assign(state, { ...resetState() })
    },

    setAliyot(state: TeacherState, aliyot: Array<AliyahType>) {
      state.aliyot = cloneRaw(aliyot)
    },

    setBarMitzvot(state: TeacherState, barMitzvot: Array<BarMitzvahType>) {
      state.barMitzvot = cloneRaw(barMitzvot)
    },

    setHaftarahToBarMitzvah(state: TeacherState, payload: { barMitzvah: BarMitzvahType; haftarah: HaftarahType }) {
      withBarMitzvah(state, payload.barMitzvah.student.username, 'setHaftarahToBarMitzvah', (bm) => {
        bm.haftarah = cloneRaw(payload.haftarah)
      })
    },

    setHaftarot(state: TeacherState, haftarot: Array<HaftarahType>) {
      state.haftarot = cloneRaw(haftarot)
    },

    setLightBarMitzvot(state: TeacherState, barMitzvot: Array<BarMitzvahType>) {
      state.lightBarMitzvot = cloneRaw(barMitzvot)
    },

    setLoading(state: TeacherState, loading: boolean) {
      state.logger?.debug && state.logger.debug(`store.teacher setting state.loading to ${loading}`)
      state.loading = loading
    },

    setLogger(state: TeacherState, logger: VueLogger) {
      state.logger = logger
    },

    setPracticeOverviews(state: TeacherState, overviews: Array<StudentPracticeOverviewType>) {
      state.practiceOverviews = cloneRaw(overviews)
    },

    setPracticeStats(state: TeacherState, stats: PracticeStatsType | null) {
      state.practiceStats = stats ? cloneRaw(stats) : null
    },

    setSections(state: TeacherState, sections: Array<SectionType>) {
      state.sections = cloneRaw(sections)
    },

    setSentenceGroupCategories(state: TeacherState, categories: Array<SentenceGroupCategoryType>) {
      state.sentenceGroupCategories = cloneRaw(categories)
    },

    setSentenceGroupSentences(
      state: TeacherState,
      payload: {
        sentenceGroup: SentenceGroupType
        sentences: Array<SentenceType>
      },
    ) {
      const sg = state.sentenceGroups.find((s: SentenceGroupType) => s.reading.id === payload.sentenceGroup.reading.id)

      if (!sg) {
        console.log('unexpected !sg in setSentenceGroupSentences')
        return
      }
      sg.sentences = cloneRaw(payload.sentences)
    },

    setSentenceGroups(state: TeacherState, sentenceGroups: Array<SentenceGroupType>) {
      state.sentenceGroups = cloneRaw(sentenceGroups)
    },

    setUpdating(state: TeacherState, updating: boolean) {
      state.logger?.debug && state.logger.debug(`store.teacher setting state.update to ${updating}`)
      state.updating = updating
    },

    unsetHaftarahFromBarMitzvah(state: TeacherState, payload: { barMitzvah: BarMitzvahType }) {
      withBarMitzvah(state, payload.barMitzvah.student.username, 'unsetHaftarahToBarMitzvah', (bm) => {
        bm.haftarah = null
      })
    },

    upsertSentenceGroupCategory(state: TeacherState, payload: { category: SentenceGroupCategoryType }) {
      upsertItem(state.sentenceGroupCategories, payload.category, (c) => c.id === payload.category.id)
    },
  },

  namespaced: true as const,
  state: resetState(),
})

export default teacher
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const teacherGetterContext = (args: [any, any, any, any]) => localGetterContext(args, teacher)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const teacherActionContext = (context: any) => localActionContext(context, teacher)
